<script setup lang="ts">
import { nextTick, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import * as Sentry from "@sentry/vue";
import { Plus } from "@lucide/vue";
import Popover from "@/components/Reusables/Popover/Popover.vue";
import Divider from "@/components/Reusables/Divider/Divider.vue";
import TagItem from "@/components/Reusables/Utils/TagItem.vue";

import { useRouter } from "vue-router";

import { useChatPanelStore } from "@stores/chat/chatPanel.store";
import { getAgentSkills, type AgentSkillSummary } from "@/api/services/AgentSkills";

const chatPanelStore = useChatPanelStore();
// The pinned skill is store state, not local: the "Use in chat" button on the AI Agent Skills cards
// sets it from outside this panel (RTLCA-4990). This component only reads it and clears it.
const { activeSkillName } = storeToRefs(chatPanelStore);

const props = withDefaults(
  defineProps<{
    isLoading: boolean;
    modelValue: string;
    // Bumped by useAgentChat's newChat() (new chat, tenant switch, panel close, empty project).
    // Watched below to clear any pinned skill so it can't leak into the next conversation.
    conversationId?: string;
    // ChatPanel narrows this to "Ask about your project..." when a project is in context. The
    // default is the project-agnostic wording, since the AI Agent also runs without one.
    placeholder?: string;
  }>(),
  { placeholder: "Ask a question..." }
);

const emit = defineEmits<{
  "update:modelValue": [value: string];
  send: [skillName?: string];
}>();

const router = useRouter();

const textareaRef = ref<HTMLTextAreaElement | null>(null);

// The skills offered by the picker: RTLCA's curated set, the tenant's shared skills, and the
// caller's own private ones (RTLCA-5655 added the curated set to both surfaces). Loaded when the
// popover actually opens rather than on every chat mount, and re-read on each open so a skill
// created or deleted meanwhile shows up. Reads the same REST list, with the same default filter, as
// the AI Agent Skills screen (RTLCA-5600) — that shared default is what keeps the two in agreement,
// so a skill the screen lists is always one this picker can pin.
const skills = ref<AgentSkillSummary[]>([]);
const isLoadingSkills = ref(false);

async function loadSkills() {
  // A reopen while the first request is still out must not fire a second one.
  if (isLoadingSkills.value) return;
  isLoadingSkills.value = true;
  try {
    skills.value = await getAgentSkills();
  } catch (e) {
    // The picker degrades to its "Create skill" row. A toast over the chat input would be noise for
    // something the user did not ask for, so the failure goes to Sentry instead.
    Sentry.captureException(e);
  } finally {
    isLoadingSkills.value = false;
  }
}

// The pin is single-shot: cleared right after send (in submit() below) and whenever the
// conversation resets (watcher below, plus the store's own close()).
function pickSkill(skill: AgentSkillSummary, close: () => void) {
  chatPanelStore.setActiveSkill(skill.name);
  close();
}

watch(
  () => props.conversationId,
  () => {
    chatPanelStore.clearActiveSkill();
  }
);

function resize() {
  const el = textareaRef.value;
  if (!el) return;
  el.style.height = "";
  if (!props.modelValue) return;
  el.style.height = `${el.scrollHeight}px`;
}

watch(
  () => props.modelValue,
  () => {
    nextTick(resize);
  },
  { immediate: true }
);

function onInput(e: Event) {
  const target = e.target as HTMLTextAreaElement;
  emit("update:modelValue", target.value);
  resize();
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === "Enter" && !e.shiftKey) {
    e.preventDefault();
    submit();
  }
}

function submit() {
  if (!props.modelValue.trim() || props.isLoading) return;
  emit("send", activeSkillName.value ?? undefined);
  // Single-shot: the pin only applies to the message that's being sent right now.
  chatPanelStore.clearActiveSkill();
  nextTick(() => {
    resize();
    textareaRef.value?.focus();
  });
}

function focus() {
  textareaRef.value?.focus();
}

defineExpose({ focus });
</script>

<template>
  <div class="relative">
    <!-- Pinned skill chip -->
    <div
      v-if="activeSkillName"
      class="mb-px-8 flex">
      <TagItem
        :label="`Using: ${activeSkillName}`"
        color="primary"
        :can-be-deleted="true"
        @on-delete="chatPanelStore.clearActiveSkill()" />
    </div>

    <div class="chat-input-shell gap-px-8 rounded-8 px-px-12 py-px-8 flex flex-col items-end border">
      <!-- Textarea -->

      <textarea
        id="chat-input-textarea"
        ref="textareaRef"
        name="chat-input"
        :value="modelValue"
        :disabled="isLoading"
        rows="1"
        :placeholder="placeholder"
        class="chat-input-field t-xs max-h-48 min-h-[2.5rem] w-full resize-none overflow-y-auto bg-transparent leading-relaxed focus:outline-none"
        :class="{ 'interactive-disabled': isLoading }"
        @input="onInput"
        @keydown="onKeydown" />
      <div class="flex w-full justify-between">
        <Popover
          placement="top-start"
          role="menu"
          aria-label="Skills"
          panel-class="w-72"
          @open="loadSkills">
          <template #trigger="{ toggle, isOpen }">
            <button
              type="button"
              class="focus-ring interactive-active t-caption-bold text-app-blue-700 gap-px-4 rounded-4 px-px-8 py-px-4 flex shrink-0 items-center whitespace-nowrap"
              :aria-expanded="isOpen"
              aria-haspopup="menu"
              @click="toggle">
              Skills
              <font-awesome-icon
                icon="fa-solid fa-chevron-down"
                class="icon-xs duration-fast ease-standard transition-transform"
                :class="{ 'rotate-180': isOpen }" />
            </button>
          </template>

          <template #default="{ close }">
            <div class="p-px-8 gap-px-4 flex flex-col">
              <p
                v-if="isLoadingSkills && !skills.length"
                class="t-footnote text-type-muted px-px-8 py-px-6">
                Loading skills…
              </p>

              <div
                v-if="skills.length"
                class="chat-scroll-area gap-px-4 flex max-h-64 flex-col overflow-y-auto">
                <!-- One step down from the default menu scale: this panel opens inside the chat
                     window, where the prompt starters are 12px and the disclosure 10px, so
                     14px/12px rows read as belonging to another surface. -->
                <button
                  v-for="skill in skills"
                  :key="skill.id"
                  type="button"
                  role="menuitem"
                  class="focus-ring rounded-4 px-px-8 py-px-6 gap-px-1 flex flex-col items-start text-left hover:bg-slate-100"
                  @click="pickSkill(skill, close)">
                  <span class="t-caption-bold text-type-default">{{ skill.name }}</span>
                  <span class="t-footnote text-type-muted w-full truncate">{{ skill.description }}</span>
                </button>
              </div>

              <Divider
                v-if="skills.length"
                spacing="sm" />

              <button
                type="button"
                role="menuitem"
                class="focus-ring rounded-4 px-px-8 py-px-6 gap-px-1 flex flex-col hover:bg-slate-100"
                @click="
                  close();
                  router.push({ name: 'agent' });
                  chatPanelStore.close();
                ">
                <span class="t-caption-bold text-type-default flex items-center whitespace-nowrap">
                  <Plus
                    :stroke-width="1.5"
                    class="icon-xs text-app-blue-900 mr-2"
                    aria-hidden="true" />Create skill</span
                >
              </button>
            </div>
          </template>
        </Popover>

        <!-- Send button -->
        <button
          type="button"
          :disabled="!modelValue.trim() || isLoading"
          :aria-label="isLoading ? 'Sending message' : 'Send message'"
          class="chat-send-button focus-ring interactive-active duration-fast ease-standard flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-opacity"
          :class="{ 'interactive-disabled': !modelValue.trim() || isLoading }"
          @click="submit">
          <span
            v-if="isLoading"
            class="block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
          <font-awesome-icon
            v-else
            :icon="['fas', 'arrow-up']"
            class="icon-xs" />
        </button>
      </div>
    </div>
  </div>
</template>
